// src/pages/SearchResults/Desktop.jsx
import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import Select, { components } from "react-select";
import {
  FaBus,
  FaClock,
  FaRoute,
  FaChevronLeft,
  FaExchangeAlt,
  FaSearch,
  FaMapMarkerAlt,
  FaCalendarAlt,
} from "react-icons/fa";

import {
  // shared context + helpers from _core
  useSearchCore,
  PALETTE,
  calculateDuration,
  getDisplayPrice,
  getReadableDate,
} from "./_core";

import BookingDeadlineTimer from "./components/BookingDeadlineTimer";
import FilterPanel from "./components/FilterPanel";
import SpecialNoticesSection from "./components/SpecialNoticesSection";

import SeatLayout from "../../components/SeatLayout";
import SeatLegend from "../../components/SeatLegend";
import BookingSummary from "../../components/BookingSummary";
// ✅ FIXED: real location is src/components/PointSelection.jsx
import PointSelection from "../../components/PointSelection";
import { API_ORIGIN } from "../../api"; // ✅ for absolute image URLs

/* ---------------- Popular Cities for quick-pick (same as Home.jsx) ---------------- */
const POPULAR_CITIES = [
  "Colombo",
  "Kandy",
  "Galle",
  "Matara",
  "Jaffna",
  "Negombo",
  "Kurunegala",
  "Gampaha",
  "Badulla",
  "Anuradhapura",
];

/* ---------------- Custom Select Menu (desktop only) (same as Home.jsx) ---------------- */
const CustomMenu = (menuKey) => {
  return (props) => {
    const { selectProps } = props;
    const recents = selectProps.recent?.[menuKey] || [];

    const finishPickAndClose = () => {
      props.selectProps.onMenuClose &&
        requestAnimationFrame(() => props.selectProps.onMenuClose());
    };

    const onPick = (city) => {
      selectProps.onChange(
        {
          value: city,
          label: city,
        },
        { action: "select-option" }
      );
      finishPickAndClose();
    };

    const handlePointerPick = (e, city) => {
      if (e.pointerType !== "touch") e.preventDefault?.();
      e.stopPropagation?.();
      onPick(city);
    };

    return (
      <components.Menu {...props}>
        {/* ===== 1) Suggested cities (typeahead results) FIRST ===== */}
        <div className="px-3 pt-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Suggested Cities
          </div>
        </div>
        <components.MenuList {...props}>{props.children}</components.MenuList>

        {/* ===== 2) Recent searches ===== */}
        <div className="px-3 pt-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-2">
            <FaClock className="opacity-70" />
            Recent searches
          </div>
          {recents.length === 0 ? (
            <div className="text-xs text-gray-400 mb-3">No recent searches</div>
          ) : (
            <div className="mb-3 divide-y rounded-lg border border-gray-100 overflow-hidden">
              {recents.map((city, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-red-50 transition"
                  style={{ touchAction: "manipulation" }}
                  onPointerDown={(e) => handlePointerPick(e, city)}
                >
                  <FaMapMarkerAlt className="text-gray-500" />
                  <span className="text-sm font-medium text-gray-800">
                    {city}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ===== 3) Popular cities LAST ===== */}
        <div className="px-3 pb-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Popular Cities
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {POPULAR_CITIES.map((c) => (
              <button
                key={c}
                type="button"
                className="px-3 py-1.5 rounded-full border text-sm hover:bg-red-50"
                style={{
                  borderColor: "#E5E7EB",
                  color: "#1F2937",
                  touchAction: "manipulation",
                }}
                onPointerDown={(e) => handlePointerPick(e, c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </components.Menu>
    );
  };
};

/* ————————————————————————————————
   Small skeleton while loading
——————————————————————————————— */
const BusCardSkeleton = () => (
  <div className="bg-white rounded-2xl p-6 animate-pulse border border-gray-300">
    <div className="flex justify-between items-start">
      <div className="flex-1 pr-4">
        <div className="h-6 w-3/5 rounded bg-gray-200 mb-4" />
        <div className="h-4 w-4/5 rounded bg-gray-200" />
      </div>
      <div className="h-10 w-24 rounded-lg bg-gray-200" />
    </div>
    <div className="border-t border-dashed my-5 border-gray-200" />
    <div className="flex justify-between items-center">
      <div className="flex gap-4">
        <div className="h-8 w-24 rounded-full bg-gray-200" />
        <div className="h-8 w-24 rounded-full bg-gray-200" />
      </div>
      <div className="h-12 w-32 rounded-lg bg-gray-200" />
    </div>
  </div>
);

const DetailsLoadingCard = () => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-8 h-8 rounded-full bg-gray-200" />
      <div className="flex-1">
        <div className="h-4 w-40 bg-gray-200 rounded mb-2" />
        <div className="h-3 w-24 bg-gray-100 rounded" />
      </div>
    </div>
    <div className="h-44 w-full rounded-lg bg-gray-100 border border-gray-200" />
    <div className="mt-3 space-y-2">
      <div className="h-3 w-5/6 bg-gray-100 rounded" />
      <div className="h-3 w-4/6 bg-gray-100 rounded" />
      <div className="h-3 w-3/6 bg-gray-100 rounded" />
    </div>
  </div>
);

const listVariants = { hidden: { opacity: 0 }, visible: { opacity: 1 } };
const itemVariants = {
  hidden: { y: 16, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

/* ──────────────────────────────────────────────────────────────
   Robust image URL normalizer (same behavior as mobile sheet)
   ────────────────────────────────────────────────────────────── */
const isAbs = (s) => /^https?:\/\//i.test(s);
const isProtocolRelative = (s) => /^\/\//.test(s);
const isRootRelative = (s) => /^\//.test(s);
const isDataUrl = (s) => /^data:/i.test(s);

function toAbsolute(src) {
  if (!src) return null;
  const s = typeof src === "string" ? src.trim() : src.url || "";
  if (!s) return null;
  if (isDataUrl(s)) return s;
  if (isAbs(s)) return s;
  if (isProtocolRelative(s)) return `https:${s}`;
  if (isRootRelative(s)) return `${API_ORIGIN}${s}`;
  return `${API_ORIGIN}/${s.replace(/^\.\//, "")}`;
}

function normalizeGallery(list = []) {
  const arr = (Array.isArray(list) ? list : []).map((item) => toAbsolute(item));
  return arr.filter(Boolean);
}

function buildDetailsLine(bus, fallbackSeatCount) {
  const parts = [
    (bus?.vehicle?.seatCount || fallbackSeatCount)
      ? `${bus?.vehicle?.seatCount || fallbackSeatCount} Seater`
      : "",
    bus?.facilities?.ac || bus?.busType === "AC" ? "AC" : "",
    bus?.features?.chargingPort || bus?.facilities?.usb ? "USB Charging" : "",
    bus?.facilities?.recliner ? "Recliner Seats" : "",
    bus?.features?.wifi ? "WiFi" : "",
    bus?.facilities?.gps ? "GPS" : "",
    bus?.facilities?.blanket ? "Blanket" : "",
    bus?.facilities?.water ? "Water" : "",
  ].filter(Boolean);

  return parts.join(" • ");
}

/* ─────────────────────────────────────────────
   Calendar helpers (Home-like popup calendar)
───────────────────────────────────────────── */
const toLocalYYYYMMDD = (dateObj) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseYMD = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

const sameYMD = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const CalendarPopover = ({
  open,
  anchorRef,
  value,
  minDateStr,
  onChange,
  onClose,
}) => {
  const popRef = useRef(null);
  const today = new Date();
  const minDate = minDateStr ? parseYMD(minDateStr) : today;
  const selected = value ? parseYMD(value) : null;

  const [viewMonth, setViewMonth] = useState(
    selected ? startOfMonth(selected) : startOfMonth(today)
  );

  useEffect(() => {
    if (!open) return;

    const onDocClick = (e) => {
      if (
        popRef.current &&
        !popRef.current.contains(e.target) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target)
      ) {
        onClose?.();
      }
    };
    const onEsc = (e) => e.key === "Escape" && onClose?.();

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, onClose, anchorRef]);

  useEffect(() => {
    if (selected) setViewMonth(startOfMonth(selected));
  }, [value]);

  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const recalc = useCallback(() => {
    const el = anchorRef?.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 360;
    let left = r.left;
    const maxLeft = window.innerWidth - width - 12;
    if (left > maxLeft) left = Math.max(12, maxLeft);
    left = Math.max(12, left);

    const top = r.bottom + 10;
    setPos({ top, left, width });
  }, [anchorRef]);

  useEffect(() => {
    if (!open) return;
    recalc();
    const onResize = () => recalc();
    const onScroll = () => recalc();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, recalc]);

  const start = startOfMonth(viewMonth);
  const end = endOfMonth(viewMonth);

  // ✅ Mon-first like Home.jsx
  const firstDow = (start.getDay() + 6) % 7;

  const totalCells = firstDow + end.getDate();
  const rows = Math.ceil(totalCells / 7);

  const cells = Array.from({ length: rows * 7 }, (_, i) => {
    const dayNum = i - firstDow + 1;
    if (dayNum < 1 || dayNum > end.getDate()) return null;
    return new Date(viewMonth.getFullYear(), viewMonth.getMonth(), dayNum);
  });

  const isDisabled = (d) => d < minDate;

  const selectDay = (d) => {
    if (!d) return;
    if (isDisabled(d)) return;
    onChange?.(toLocalYYYYMMDD(d));
    onClose?.();
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9998]">
      {/* click outside */}
      <div
        className="absolute inset-0 bg-transparent"
        onMouseDown={(e) => {
          if (popRef.current && popRef.current.contains(e.target)) return;
          onClose?.();
        }}
      />

      <AnimatePresence>
        <motion.div
          ref={popRef}
          initial={{ opacity: 0, y: -8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.98 }}
          transition={{ duration: 0.18 }}
          className="fixed z-[9999] bg-white rounded-2xl shadow-[0_15px_40px_-10px_rgba(0,0,0,0.25)] border border-gray-100 overflow-hidden"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
        >
          {/* Header */}
          <div
            className="px-4 pt-4 pb-2 border-b"
            style={{ borderColor: PALETTE.borderLight }}
          >
            <div
              className="text-xs uppercase font-medium tracking-wider"
              style={{ color: PALETTE.textLight }}
            >
              Date of Journey
            </div>

            <div className="flex items-center justify-between mt-1">
              <div
                className="text-base font-semibold"
                style={{ color: PALETTE.textDark }}
              >
                {value
                  ? parseYMD(value).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                  : "Select Date"}
              </div>

              <div className="flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.06 }}
                  whileTap={{ scale: 0.96 }}
                  type="button"
                  onClick={() => setViewMonth(addMonths(viewMonth, -1))}
                  className="group w-9 h-9 rounded-full border bg-gray-50 hover:bg-red-50 transition flex items-center justify-center active:scale-[0.98] shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                  style={{ borderColor: PALETTE.borderLight }}
                  aria-label="Previous month"
                >
                  <FaChevronLeft
                    className="text-[15px] transition-colors"
                    style={{ color: PALETTE.textDark }}
                  />
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.06 }}
                  whileTap={{ scale: 0.96 }}
                  type="button"
                  onClick={() => setViewMonth(addMonths(viewMonth, 1))}
                  className="group w-9 h-9 rounded-full border bg-gray-50 hover:bg-red-50 transition flex items-center justify-center active:scale-[0.98] shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                  style={{ borderColor: PALETTE.borderLight }}
                  aria-label="Next month"
                >
                  <FaChevronLeft
                    className="text-[15px] rotate-180 transition-colors"
                    style={{ color: PALETTE.textDark }}
                  />
                </motion.button>
              </div>
            </div>

            <div
              className="mt-1 text-sm font-medium"
              style={{ color: PALETTE.textDark }}
            >
              {viewMonth.toLocaleString("en-GB", {
                month: "long",
                year: "numeric",
              })}
            </div>
          </div>

          {/* Weekdays (Mon-first) */}
          <div
            className="grid grid-cols-7 text-center text-xs py-2"
            style={{ color: PALETTE.textLight }}
          >
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="select-none">
                {d}
              </div>
            ))}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7 gap-y-1 px-2 pb-3">
            {cells.map((d, idx) => {
              if (!d) return <div key={idx} />;

              const selectedDay = value && sameYMD(parseYMD(value), d);
              const isToday = sameYMD(new Date(), d);
              const disabled = isDisabled(d);

              let classes =
                "mx-auto my-1 flex items-center justify-center w-9 h-9 rounded-full text-sm transition select-none ";
              if (disabled) classes += "text-gray-300 cursor-not-allowed";
              else if (selectedDay) classes += "text-white font-semibold";
              else classes += "hover:bg-red-50 cursor-pointer";

              return (
                <button
                  key={idx}
                  type="button"
                  className={classes}
                  onClick={() => selectDay(d)}
                  style={{
                    backgroundColor: selectedDay ? PALETTE.primaryRed : undefined,
                    border:
                      isToday && !selectedDay
                        ? `1px solid ${PALETTE.primaryRed}`
                        : undefined,
                    color: disabled
                      ? "#D1D5DB"
                      : selectedDay
                      ? "#FFFFFF"
                      : PALETTE.textDark,
                  }}
                  disabled={disabled}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          {/* Footer (Today + Tomorrow) */}
          <div
            className="px-4 py-3 border-t flex items-center justify-between"
            style={{ borderColor: PALETTE.borderLight }}
          >
            <div className="space-x-3">
              <button
                type="button"
                onClick={() => {
                  const t = new Date();
                  const ymd = toLocalYYYYMMDD(t);
                  if (minDateStr && parseYMD(ymd) < minDate) return;
                  onChange?.(ymd);
                  onClose?.();
                }}
                className="text-sm font-medium"
                style={{ color: PALETTE.accentBlue }}
              >
                Today
              </button>

              <button
                type="button"
                onClick={() => {
                  const tm = new Date();
                  tm.setDate(tm.getDate() + 1);
                  const ymd = toLocalYYYYMMDD(tm);
                  if (minDateStr && parseYMD(ymd) < minDate) return;
                  onChange?.(ymd);
                  onClose?.();
                }}
                className="text-sm font-medium"
                style={{ color: PALETTE.accentBlue }}
              >
                Tomorrow
              </button>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="text-xs px-3 py-1.5 rounded-lg border hover:bg-gray-50"
              style={{
                borderColor: PALETTE.borderLight,
                color: PALETTE.textLight,
              }}
            >
              Close
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>,
    document.body
  );
};

/* ————————————————————————————————
   Step guide bar (desktop card) — visual only (no extra buttons)
——————————————————————————————— */
const StepGuideBar = ({ activeStep = 1 }) => {
  const steps = [
    { id: 1, label: "Select seats" },
    { id: 2, label: "Board & drop" },
    { id: 3, label: "Booking summary" },
  ];

  return (
    <div className="w-full">
      <div className="flex items-center w-full">
        {steps.map((s, idx) => {
          const done = activeStep > s.id;
          const active = activeStep === s.id;

          const node = (
            <div className="flex items-center gap-2 min-w-0">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border transition-all duration-300 ${
                  active ? "animate-[rbSoftPulse_1.8s_ease-in-out_infinite]" : ""
                }`}
                style={{
                  backgroundColor: done || active ? PALETTE.primaryRed : "#fff",
                  borderColor:
                    done || active ? PALETTE.primaryRed : PALETTE.borderLight,
                  color: done || active ? "#fff" : PALETTE.textLight,
                  boxShadow: active
                    ? "0 0 0 3px rgba(248,113,113,0.10)"
                    : "none",
                }}
              >
                {s.id}
              </div>
              <div
                className={`text-xs font-semibold truncate transition-all duration-300 ${
                  active ? "animate-[rbSoftPulse_1.8s_ease-in-out_infinite]" : ""
                }`}
                style={{
                  color: active ? PALETTE.primaryRed : PALETTE.textLight,
                }}
              >
                {s.label}
              </div>
            </div>
          );

          // ✅ FIX: distribute spacing evenly by keeping connectors outside step blocks
          if (idx < steps.length - 1) {
            return (
              <div key={s.id} className="flex items-center flex-1 min-w-0">
                {node}
                <div
                  className={`h-[2px] flex-1 rounded-full mx-3 transition-all duration-300 ${
                    active ? "animate-[rbSoftPulse_1.8s_ease-in-out_infinite]" : ""
                  }`}
                  style={{
                    backgroundColor: done
                      ? PALETTE.primaryRed
                      : active
                      ? "#FCA5A5"
                      : PALETTE.borderLight,
                    opacity: done ? 1 : active ? 1 : 0.9,
                  }}
                />
              </div>
            );
          }

          return (
            <div key={s.id} className="flex items-center min-w-0">
              {node}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ————————————————————————————————
   Child card component (prevents hooks-in-loop)
——————————————————————————————— */
function BusCard({
  bus,
  detailBus,
  isDetailsLoading,
  busKey,
  from,
  to,
  searchDateParam,
  availabilityEntry,
  expandedBusId,
  busSpecificBookingData,
  handleToggleSeatLayout,
  handleSeatToggle,
  handleBoardingPointSelect,
  handleDroppingPointSelect,
  handleProceedToPayment,
  fetchData,
  refreshAvailability,
}) {
  const resolvedBus = detailBus || bus;
  const displayPrice = getDisplayPrice(resolvedBus, from, to);

  // Timer within 12h window
  let timerProps = null;
  if (searchDateParam && bus.departureTime) {
    const now = new Date();
    const [h, m] = bus.departureTime.split(":").map(Number);
    const [yy, mm, dd] = searchDateParam.split("-").map(Number);
    const dep = new Date(yy, mm - 1, dd, h, m).getTime();
    const diffHrs = (dep - now.getTime()) / (1000 * 60 * 60);
    if (diffHrs > 0 && diffHrs <= 12) {
      timerProps = {
        deadlineTimestamp: dep - 60 * 60 * 1000,
        departureTimestamp: dep,
        onDeadline: () => refreshAvailability([bus], { force: true }),
      };
    }
  }

  const a = availabilityEntry;
  const fallbackAvailableSeats =
    typeof bus?.availableSeats === "number" ? bus.availableSeats : null;
  const fallbackAvailableWindowSeats =
    typeof bus?.availableWindowSeats === "number"
      ? bus.availableWindowSeats
      : null;
  const availableSeats =
    typeof a?.available === "number" ? a.available : fallbackAvailableSeats;
  const availableWindowSeats =
    typeof a?.window === "number" ? a.window : fallbackAvailableWindowSeats;
  const hasAvailability = typeof availableSeats === "number";
  const canBook = bus?.canBook !== false && bus?.listingMode !== "timetableOnly";
  const seatCountToShow = canBook
    ? hasAvailability
      ? availableSeats
      : Number(bus?.vehicle?.seatCount || 0)
    : Number(bus?.vehicle?.seatCount || 0);
  const detailsLine = buildDetailsLine(bus, seatCountToShow);
  const isSoldOut = canBook && hasAvailability && availableSeats === 0;

  const currentBusBookingData = busSpecificBookingData[busKey] || {
    selectedSeats: [],
    seatGenders: {},
    selectedBoardingPoint: null,
    selectedDroppingPoint: null,
    basePrice: 0,
    convenienceFee: 0,
    totalPrice: 0,
  };

  const activeStep = useMemo(() => {
    const hasSeats = (currentBusBookingData.selectedSeats || []).length > 0;
    const hasPoints =
      !!currentBusBookingData.selectedBoardingPoint &&
      !!currentBusBookingData.selectedDroppingPoint;

    if (hasSeats && hasPoints) return 3;
    if (hasSeats) return 2;
    return 1;
  }, [
    currentBusBookingData.selectedSeats,
    currentBusBookingData.selectedBoardingPoint,
    currentBusBookingData.selectedDroppingPoint,
  ]);

  const hasSeats = (currentBusBookingData.selectedSeats || []).length > 0;
  const hasBoarding = !!currentBusBookingData.selectedBoardingPoint;
  const hasDropping = !!currentBusBookingData.selectedDroppingPoint;

  const getSectionStateClass = (section) => {
    const base =
      "rounded-xl border bg-white transition-all duration-300 shadow-[0_1px_2px_rgba(16,24,40,0.04)]";

    const active =
      "border-gray-200 ring-0 shadow-[0_6px_20px_rgba(16,24,40,0.06)] animate-none";

    const done =
      "border-red-200 bg-red-50/[0.35] shadow-[0_2px_10px_rgba(220,38,38,0.04)]";

    const idle = "border-gray-200";

    if (section === "seat") {
      if (activeStep === 1) return `${base} ${active}`;
      if (hasSeats) return `${base} ${done}`;
      return `${base} ${idle}`;
    }

    if (section === "boarding") {
      if (activeStep === 2 && !hasBoarding) return `${base} ${active}`;
      if (hasBoarding) return `${base} ${done}`;
      return `${base} ${idle}`;
    }

    if (section === "dropping") {
      if (activeStep === 2 && hasBoarding && !hasDropping)
        return `${base} ${active}`;
      if (hasDropping) return `${base} ${done}`;
      return `${base} ${idle}`;
    }

    if (section === "summary") {
      if (activeStep === 3)
        return `${base} border-red-200 ring-1 ring-red-200/50 shadow-[0_4px_14px_rgba(220,38,38,0.05)] animate-[rbSoftPulse_1.8s_ease-in-out_infinite]`;
      if (hasSeats && hasBoarding && hasDropping)
        return `${base} border-red-100 bg-red-50/[0.22]`;
      return `${base} ${idle}`;
    }

    return `${base} ${idle}`;
  };

  const hasStrike =
    typeof bus.originalPrice === "number" && bus.originalPrice > displayPrice;

  // ✅ Memoize media per-bus
  const { coverUrl, gallery } = useMemo(() => {
    const coverRaw =
      resolvedBus?.media?.cover?.url ||
      resolvedBus?.cover ||
      resolvedBus?.coverPhoto ||
      resolvedBus?.coverImage ||
      resolvedBus?.images?.cover ||
      null;

    const galleryRaw =
      (Array.isArray(resolvedBus?.media?.gallery) && resolvedBus.media.gallery) ||
      (Array.isArray(resolvedBus?.galleryPhotos) && resolvedBus.galleryPhotos) ||
      (Array.isArray(resolvedBus?.gallery) && resolvedBus.gallery) ||
      (Array.isArray(resolvedBus?.images) && resolvedBus.images) ||
      [];

    const normalized = Object.freeze(
      normalizeGallery(galleryRaw.map((g) => (typeof g === "string" ? g : g?.url)))
    );

    return {
      coverUrl: toAbsolute(coverRaw),
      gallery: normalized,
    };
  }, [busKey, resolvedBus]);

  // Local details toggle per card
  const [detailsOpen, setDetailsOpen] = useState(false);

  // facility chip
  const facilityChip = (label) => (
    <span
      key={label}
      className="inline-block text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 border mr-1 mb-1"
    >
      {label}
    </span>
  );

  // Memoized photos grid
  const photosGrid = useMemo(() => {
    if (!gallery?.length) return null;
    return (
      <div className="mt-3">
        <p className="font-medium mb-2">Photos</p>
        <div className="grid grid-cols-3 gap-2">
          {gallery.slice(0, 6).map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`Bus ${i + 1}`}
              className="w-full h-24 object-contain rounded border bg-white p-1"
              loading="lazy"
              decoding="async"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          ))}
        </div>
      </div>
    );
  }, [gallery]);

  return (
    <>
      <style>
        {`
          @keyframes rbSoftPulse {
            0%, 100% { box-shadow: 0 1px 2px rgba(16,24,40,0.04), 0 0 0 0 rgba(220,38,38,0.00); }
            50% { box-shadow: 0 8px 24px rgba(220,38,38,0.08), 0 0 0 3px rgba(248,113,113,0.10); }
          }
          @keyframes rbMarquee {
            0% { transform: translateX(0%); }
            100% { transform: translateX(-50%); }
          }
          .rb-card { transition: box-shadow 0.22s ease, transform 0.22s ease; }
          .rb-card:hover { box-shadow: 0 8px 32px rgba(0,0,0,0.07) !important; transform: translateY(-1px); }
          .rb-book-btn { transition: opacity 0.18s ease, transform 0.18s ease; }
          .rb-book-btn:hover:not(:disabled) { opacity: 0.88; transform: scale(1.02); }
          .rb-book-btn:active:not(:disabled) { transform: scale(0.98); }
        `}
      </style>

      <motion.div
        variants={itemVariants}
        className={`rb-card mb-3 ${isSoldOut ? "opacity-55" : ""}`}
        style={{
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #F0F0F0",
          boxShadow: "0 2px 12px rgba(0,0,0,0.045)",
          overflow: "hidden",
        }}
      >
        {/* Desktop card */}
        <div style={{ padding: "20px 24px 22px" }}>

          {/* ── ROW 1: Status badge + Logo ── */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>

            {/* Left: badge + name + pills */}
            <div style={{ flex: 1, minWidth: 0, paddingRight: 16 }}>

              {/* Status badge */}
              {canBook ? (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginBottom: 8 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16A34A", display: "inline-block" }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#16A34A", letterSpacing: "0.02em" }}>Online booking available</span>
                </div>
              ) : (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginBottom: 8 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#C74A50", display: "inline-block" }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#C74A50", letterSpacing: "0.02em" }}>Timetable Only</span>
                </div>
              )}

              {/* Operator name */}
              <p style={{ fontSize: 15, fontWeight: 700, color: "#111", margin: "0 0 8px", letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {bus.name}
              </p>

              {/* Pills row */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                {bus.busType ? (
                  <span style={{ fontSize: 11, fontWeight: 500, color: "#2563EB", background: "#EFF6FF", borderRadius: 6, padding: "2px 8px" }}>
                    {bus.busType}
                  </span>
                ) : null}

                {typeof seatCountToShow === "number" && (seatCountToShow > 0 || (canBook && isSoldOut)) && (
                  <span style={{ fontSize: 11, fontWeight: 500, color: "#DC2626", background: "#FEF2F2", borderRadius: 6, padding: "2px 8px" }}>
                    {canBook
                      ? hasAvailability
                        ? seatCountToShow === 0 ? "Sold Out" : `${seatCountToShow} Seats Left`
                        : `${seatCountToShow} Seats`
                      : `${seatCountToShow} Seats`}
                  </span>
                )}

                {canBook && typeof availableWindowSeats === "number" && availableWindowSeats > 0 ? (
                  <span style={{ fontSize: 11, fontWeight: 500, color: "#555", background: "#F5F5F5", borderRadius: 6, padding: "2px 8px" }}>
                    {availableWindowSeats} Window
                  </span>
                ) : null}

                {bus.liveTracking && (
                  <span style={{ fontSize: 11, fontWeight: 500, color: "#7C3AED", background: "#F5F3FF", borderRadius: 6, padding: "2px 8px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <FaRoute style={{ fontSize: 9 }} /> Live
                  </span>
                )}
              </div>
            </div>

            {/* Logo */}
            <div style={{ flexShrink: 0 }}>
              {bus.operatorLogo ? (
                <div style={{ width: 44, height: 44, borderRadius: 12, border: "1px solid #F0F0F0", background: "#FAFAFA", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <img
                    src={toAbsolute(bus.operatorLogo)}
                    alt={`${bus.name} logo`}
                    style={{ width: 36, height: 36, objectFit: "contain" }}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                </div>
              ) : (
                <div style={{ width: 44, height: 44, borderRadius: 12, border: "1px solid #F0F0F0", background: "#FAFAFA", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FaBus style={{ fontSize: 18, color: "#ccc" }} />
                </div>
              )}
            </div>
          </div>

          {/* ── DIVIDER ── */}
          <div style={{ height: 1, background: "#F3F3F3", margin: "0 0 18px" }} />

          {/* ── ROW 2: Time strip + Price + CTA ── */}
          <div style={{ display: "flex", alignItems: "center", gap: 0 }}>

            {/* Departure */}
            <div style={{ minWidth: 80 }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#111", letterSpacing: "-0.03em", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                {bus.departureTime}
              </div>
              <div style={{ fontSize: 10, color: "#aaa", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600 }}>
                Departure
              </div>
            </div>

            {/* Duration connector */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: "0 12px" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#999", marginBottom: 6, letterSpacing: "0.02em" }}>
                {calculateDuration(bus.departureTime, bus.arrivalTime)}
              </div>
              <div style={{ display: "flex", alignItems: "center", width: "100%", gap: 0 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", border: "1.5px solid #D1D5DB", background: "#fff", flexShrink: 0 }} />
                <div style={{ flex: 1, height: 1, background: "repeating-linear-gradient(90deg, #D1D5DB 0px, #D1D5DB 4px, transparent 4px, transparent 8px)" }} />
                <div style={{ width: 6, height: 6, borderRadius: "50%", border: "1.5px solid #D1D5DB", background: "#fff", flexShrink: 0 }} />
              </div>
            </div>

            {/* Arrival */}
            <div style={{ minWidth: 80, textAlign: "right" }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#111", letterSpacing: "-0.03em", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                {bus.arrivalTime}
              </div>
              <div style={{ fontSize: 10, color: "#aaa", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600 }}>
                Arrival
              </div>
            </div>

            {/* Vertical separator */}
            <div style={{ width: 1, height: 44, background: "#F0F0F0", margin: "0 20px", flexShrink: 0 }} />

            {/* Price */}
            <div style={{ textAlign: "right", flexShrink: 0, marginRight: 20 }}>
              {hasStrike && (
                <div style={{ fontSize: 10, color: "#bbb", textDecoration: "line-through", marginBottom: 1 }}>
                  Rs. {bus.originalPrice}
                </div>
              )}
              <div style={{ display: "flex", alignItems: "baseline", gap: 2, justifyContent: "flex-end" }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#DC2626" }}>Rs.</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: "#DC2626", letterSpacing: "-0.03em", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                  {displayPrice}
                </span>
              </div>
              <div style={{ fontSize: 10, color: "#bbb", marginTop: 2, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
                Onwards
              </div>
            </div>

            {/* CTA Button */}
            <div style={{ flexShrink: 0 }}>
              {canBook ? (
                <button
                  onClick={() => handleToggleSeatLayout(bus)}
                  disabled={isSoldOut}
                  className="rb-book-btn"
                  style={{
                    width: 130,
                    padding: "11px 0",
                    borderRadius: 12,
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#fff",
                    background: isSoldOut ? "#D1D5DB" : "#DC2626",
                    border: "none",
                    cursor: isSoldOut ? "not-allowed" : "pointer",
                    letterSpacing: "0.01em",
                    boxShadow: isSoldOut ? "none" : "0 4px 16px rgba(220,38,38,0.25)",
                    opacity: isSoldOut ? 0.6 : 1,
                  }}
                >
                  {isSoldOut ? "Sold Out" : expandedBusId === busKey ? "Hide Seats" : "Book Seats"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setDetailsOpen((v) => !v)}
                  className="rb-book-btn"
                  style={{
                    width: 130,
                    padding: "11px 0",
                    borderRadius: 12,
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#DC2626",
                    background: "#FEF2F2",
                    border: "1px solid #FECACA",
                    cursor: "pointer",
                    letterSpacing: "0.01em",
                  }}
                >
                  {detailsOpen ? "Hide Details" : "View Details"}
                </button>
              )}
            </div>
          </div>

          {/* ── Details chip row + timer aligned under their respective columns ── */}
          {(detailsLine || (canBook && timerProps)) && (
            <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 0 }}>
              {/* Left: facilities chips — flex:1 mirrors the [Departure + connector + Arrival] block */}
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                {detailsLine && detailsLine.split(" • ").map((chip, i) => (
                  <span key={i} style={{ fontSize: 11, fontWeight: 500, color: "#6B7280", background: "#F7F7F8", borderRadius: 6, padding: "3px 8px", border: "1px solid #EFEFEF" }}>
                    {chip}
                  </span>
                ))}
              </div>
              {/* Right: timer — sits under the separator + price + button zone */}
              {canBook && timerProps && (
                <div style={{ flexShrink: 0, paddingLeft: 20 }}>
                  <span
                    style={{ fontSize: 11, fontWeight: 600, color: "#B45309", background: "#FFF7ED", borderRadius: 6, padding: "3px 8px", border: "1px solid #FDE68A", display: "inline-flex", alignItems: "center", gap: 4 }}
                  >
                    <BookingDeadlineTimer {...timerProps} />
                  </span>
                </div>
              )}
            </div>
          )}

          {canBook && expandedBusId === busKey && isDetailsLoading && (
            <div className="mt-3">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                Loading full bus details...
              </span>
            </div>
          )}

          {!canBook && detailsOpen && (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="mt-5 pt-5"
                style={{ borderTop: "1px solid #F3F3F3" }}
              >
                <div style={{ background: "#FAFAFA", borderRadius: 12, border: "1px solid #F0F0F0", padding: 16 }}>
                  <div className="flex items-center gap-2 mb-2">
                    {bus.operatorLogo ? (
                      <img
                        src={toAbsolute(bus.operatorLogo)}
                        alt="operator"
                        className="w-6 h-6 object-contain rounded"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    ) : (
                      <FaBus className="text-gray-500" />
                    )}
                    <h4 className="text-sm font-semibold text-gray-900">
                      Bus details
                    </h4>
                  </div>

                  {coverUrl && (
                    <img
                      src={coverUrl}
                      alt={`${resolvedBus.name} cover`}
                      className="w-full h-44 object-cover rounded-md border"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  )}

                  <div className="mt-3 text-sm text-gray-700">
                    <p className="text-sm font-semibold text-gray-900 mb-1">
                      Booking not available yet
                    </p>
                    <p className="text-xs text-gray-500 mb-3">
                      This bus is currently shown as timetable only.
                    </p>

                    {resolvedBus?.owner?.name ? (
                      <p className="mb-2">
                        <span className="font-medium">Owner:</span> {resolvedBus.owner.name}
                      </p>
                    ) : null}

                    {resolvedBus.showPublicContact && resolvedBus.ownerNotifyMobile ? (
                      <p className="mb-2">
                        <span className="font-medium">Contact:</span> {resolvedBus.ownerNotifyMobile}
                      </p>
                    ) : null}

                    {resolvedBus.showPublicContact && !resolvedBus.ownerNotifyMobile && resolvedBus.conductor?.mobile ? (
                      <p className="mb-2">
                        <span className="font-medium">Contact:</span> {resolvedBus.conductor.mobile}
                      </p>
                    ) : null}

                    {resolvedBus.showPublicContact && !resolvedBus.ownerNotifyMobile && !resolvedBus.conductor?.mobile && resolvedBus.conductor?.altMobile ? (
                      <p className="mb-2">
                        <span className="font-medium">Alt Contact:</span> {resolvedBus.conductor.altMobile}
                      </p>
                    ) : null}

                    {(resolvedBus?.routeMeta?.via?.length ||
                      resolvedBus?.routeMeta?.distanceKm ||
                      resolvedBus?.routeMeta?.durationMin) && (
                      <div className="mb-3">
                        {Array.isArray(resolvedBus?.routeMeta?.via) &&
                          resolvedBus.routeMeta.via.length > 0 && (
                            <p className="mb-1">
                              <span className="font-medium">Via:</span>{" "}
                              {resolvedBus.routeMeta.via.join(", ")}
                            </p>
                          )}
                        {(bus?.routeMeta?.distanceKm ||
                          resolvedBus?.routeMeta?.durationMin) && (
                          <p className="mb-1">
                            {resolvedBus?.routeMeta?.distanceKm ? (
                              <span className="mr-3">
                                <span className="font-medium">Distance:</span>{" "}
                                {resolvedBus.routeMeta.distanceKm} km
                              </span>
                            ) : null}
                            {resolvedBus?.routeMeta?.durationMin ? (
                              <span>
                                <span className="font-medium">Duration:</span>{" "}
                                {resolvedBus.routeMeta.durationMin} min
                              </span>
                            ) : null}
                          </p>
                        )}
                      </div>
                    )}

                    {resolvedBus?.facilities && (
                      <div className="mb-3">
                        <p className="font-medium mb-1">Facilities</p>
                        <div>
                          {Object.entries(resolvedBus.facilities)
                            .filter(([k, v]) => typeof v === "boolean" && v)
                            .map(([k]) =>
                              facilityChip(k.replace(/([A-Z])/g, " $1"))
                            )}

                          {Array.isArray(resolvedBus.facilities?.extraTags) &&
                            resolvedBus.facilities.extraTags.map((t) =>
                              facilityChip(t)
                            )}
                        </div>
                      </div>
                    )}

                    {resolvedBus?.vehicle &&
                      (resolvedBus.vehicle.registrationNo ||
                        resolvedBus.vehicle.make ||
                        resolvedBus.vehicle.model ||
                        resolvedBus.vehicle.seatCount) && (
                        <div className="mb-3">
                          <p className="font-medium mb-1">Vehicle</p>
                          <p className="text-gray-700">
                            {[
                              resolvedBus.vehicle.registrationNo ? (
                                <span key="reg" className="font-semibold">
                                  {resolvedBus.vehicle.registrationNo}
                                </span>
                              ) : null,
                              resolvedBus.vehicle.make,
                              resolvedBus.vehicle.model,
                              resolvedBus.vehicle.seatCount
                                ? `${bus.vehicle.seatCount} seats`
                                : null,
                            ]
                              .filter(Boolean)
                              .map((node, i) =>
                                typeof node === "string" ? (
                                  <span key={i}>
                                    {i > 0 ? " • " : ""}
                                    {node}
                                  </span>
                                ) : (
                                  <span key={i}>
                                    {i > 0 ? " • " : ""}
                                    {node}
                                  </span>
                                )
                              )}
                          </p>
                        </div>
                      )}

                    {resolvedBus.details && (
                      <div className="mb-3">
                        <p className="font-medium mb-1">About this bus</p>
                        <p className="text-gray-700 whitespace-pre-line">
                          {resolvedBus.details}
                        </p>
                      </div>
                    )}
                    {resolvedBus.detailsHtml && (
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: resolvedBus.detailsHtml,
                        }}
                      />
                    )}

                    {photosGrid}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          )}

          {canBook && expandedBusId === busKey && (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="mt-6 pt-6"
                style={{ borderTop: "1px solid #F3F3F3" }}
              >
                <div className="mb-4 xl:mb-5 xl:max-w-[760px]">
                  <StepGuideBar activeStep={activeStep} />
                </div>
                <div className="grid grid-cols-1 xl:[grid-template-columns:minmax(0,1fr)_380px] gap-6 xl:items-stretch items-start">
                  <div className="min-w-0 flex flex-col gap-4">
                    <div className={`${getSectionStateClass("seat")} bg-gray-50 p-3 sm:p-4 h-full`}>
                      <SeatLegend />
                      <SeatLayout
                        seatLayout={bus.seatLayout}
                        bookedSeats={[...(a?.bookedSeats || [])]}
                        selectedSeats={currentBusBookingData.selectedSeats}
                        onSeatClick={(seat) => handleSeatToggle(bus, seat)}
                        bookedSeatGenders={a?.seatGenderMap || {}}
                        selectedSeatGenders={{}}
                      />
                    </div>

                    {/* ✅ Cover + expandable “Why book this bus?” details */}
                    {(coverUrl || resolvedBus.details || resolvedBus.detailsHtml || isDetailsLoading) && (
                      <div style={{ background: "#FAFAFA", padding: 12, borderRadius: 12, border: "1px solid #F0F0F0" }}>
                        {/* Title row with logo */}
                        <div className="flex items-center gap-2 mb-2">
                          {bus.operatorLogo ? (
                            <img
                              src={toAbsolute(resolvedBus.operatorLogo)}
                              alt="operator"
                              className="w-6 h-6 object-contain rounded"
                              loading="lazy"
                              decoding="async"
                              onError={(e) =>
                                (e.currentTarget.style.display = "none")
                              }
                            />
                          ) : (
                            <FaBus className="text-gray-500" />
                          )}
                          <h4 className="text-sm font-semibold text-gray-900">
                            Why book this bus?
                          </h4>
                        </div>

                        {coverUrl && (
                          <img
                            src={coverUrl}
                            alt={`${resolvedBus.name} cover`}
                            className="w-full h-44 object-cover rounded-md border"
                            loading="lazy"
                            decoding="async"
                            onError={(e) =>
                              (e.currentTarget.style.display = "none")
                            }
                          />
                        )}
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={() => setDetailsOpen((v) => !v)}
                            className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                          >
                            {detailsOpen ? "Hide details" : "Know more details"}
                          </button>
                        </div>

                        <AnimatePresence>
                          {detailsOpen && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.25 }}
                              className="mt-3 text-sm text-gray-700"
                            >
                              {isDetailsLoading ? (
                                <DetailsLoadingCard />
                              ) : (
                              <>
                              {/* Owner inline (mirrors mobile) */}
                              {resolvedBus?.owner?.name ? (
                                <p className="mb-2">
                                  <span className="font-medium">Owner:</span>{" "}
                                  {resolvedBus.owner.name}
                                </p>
                              ) : null}

                              {/* Route meta */}
                              {(resolvedBus?.routeMeta?.via?.length ||
                                resolvedBus?.routeMeta?.distanceKm ||
                                resolvedBus?.routeMeta?.durationMin) && (
                                <div className="mb-3">
                                  {Array.isArray(resolvedBus?.routeMeta?.via) &&
                                    resolvedBus.routeMeta.via.length > 0 && (
                                      <p className="mb-1">
                                        <span className="font-medium">Via:</span>{" "}
                                        {resolvedBus.routeMeta.via.join(", ")}
                                      </p>
                                    )}
                                  {(bus?.routeMeta?.distanceKm ||
                                    resolvedBus?.routeMeta?.durationMin) && (
                                    <p className="mb-1">
                                      {resolvedBus?.routeMeta?.distanceKm ? (
                                        <span className="mr-3">
                                          <span className="font-medium">
                                            Distance:
                                          </span>{" "}
                                          {resolvedBus.routeMeta.distanceKm} km
                                        </span>
                                      ) : null}
                                      {resolvedBus?.routeMeta?.durationMin ? (
                                        <span>
                                          <span className="font-medium">
                                            Duration:
                                          </span>{" "}
                                          {resolvedBus.routeMeta.durationMin} min
                                        </span>
                                      ) : null}
                                    </p>
                                  )}
                                </div>
                              )}

                              {/* Facilities (expanded) */}
                              {resolvedBus?.facilities && (
                                <div className="mb-3">
                                  <p className="font-medium mb-1">Facilities</p>
                                  <div>
                                    {Object.entries(resolvedBus.facilities)
                                      .filter(([k, v]) => typeof v === "boolean" && v)
                                      .map(([k]) =>
                                        facilityChip(k.replace(/([A-Z])/g, " $1"))
                                      )}

                                    {Array.isArray(resolvedBus.facilities?.extraTags) &&
                                      resolvedBus.facilities.extraTags.map((t) =>
                                        facilityChip(t)
                                      )}
                                  </div>
                                </div>
                              )}

                              {/* Vehicle */}
                              {resolvedBus?.vehicle &&
                                (resolvedBus.vehicle.registrationNo ||
                                  resolvedBus.vehicle.make ||
                                  resolvedBus.vehicle.model ||
                                  resolvedBus.vehicle.seatCount) && (
                                  <div className="mb-3">
                                    <p className="font-medium mb-1">Vehicle</p>
                                    <p className="text-gray-700">
                                      {[
                                        resolvedBus.vehicle.registrationNo ? (
                                          <span key="reg" className="font-semibold">
                                            {resolvedBus.vehicle.registrationNo}
                                          </span>
                                        ) : null,
                                        resolvedBus.vehicle.make,
                                        resolvedBus.vehicle.model,
                                        resolvedBus.vehicle.seatCount
                                          ? `${bus.vehicle.seatCount} seats`
                                          : null,
                                      ]
                                        .filter(Boolean)
                                        .map((node, i) =>
                                          typeof node === "string" ? (
                                            <span key={i}>
                                              {i > 0 ? " • " : ""}
                                              {node}
                                            </span>
                                          ) : (
                                            <span key={i}>
                                              {i > 0 ? " • " : ""}
                                              {node}
                                            </span>
                                          )
                                        )}
                                    </p>
                                  </div>
                                )}

                              {/* Details text/html */}
                              {resolvedBus.details && (
                                <div className="mb-3">
                                  <p className="font-medium mb-1">About this bus</p>
                                  <p className="text-gray-700 whitespace-pre-line">
                                    {resolvedBus.details}
                                  </p>
                                </div>
                              )}
                              {resolvedBus.detailsHtml && (
                                <div
                                  className="prose prose-sm max-w-none"
                                  dangerouslySetInnerHTML={{
                                    __html: resolvedBus.detailsHtml,
                                  }}
                                />
                              )}

                              {/* Gallery (memoized content) */}
                              {photosGrid}
                              </>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>

                  <div className="xl:w-[380px] xl:ml-auto h-full">
                    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden h-full xl:min-h-[760px] flex flex-col">
                      <div className="px-4 pt-4 pb-3 border-b border-gray-100">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              Complete Your Booking
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Choose points and review fare
                            </p>
                          </div>
                          <span className="inline-flex items-center rounded-full bg-red-50 text-red-600 text-[11px] font-semibold px-2.5 py-1 whitespace-nowrap">
                            Step {activeStep}/3
                          </span>
                        </div>
                      </div>

                      <div className="px-3 pt-3 pb-2 flex-1 min-h-0 overflow-hidden">
                        <PointSelection
                          boardingPoints={resolvedBus.boardingPoints}
                          droppingPoints={resolvedBus.droppingPoints}
                          selectedBoardingPoint={
                            currentBusBookingData.selectedBoardingPoint
                          }
                          setSelectedBoardingPoint={(p) =>
                            handleBoardingPointSelect(resolvedBus, p)
                          }
                          selectedDroppingPoint={
                            currentBusBookingData.selectedDroppingPoint
                          }
                          setSelectedDroppingPoint={(p) =>
                            handleDroppingPointSelect(resolvedBus, p)
                          }
                        />
                      </div>

                      <div className="px-3 pb-3 pt-2 border-t border-gray-100 mt-auto">
                        <BookingSummary
                          bus={resolvedBus}
                          selectedSeats={currentBusBookingData.selectedSeats}
                          date={searchDateParam}
                          basePrice={currentBusBookingData.basePrice}
                          convenienceFee={currentBusBookingData.convenienceFee}
                          totalPrice={currentBusBookingData.totalPrice}
                          onProceed={() => handleProceedToPayment(resolvedBus)}
                          boardingPoint={currentBusBookingData.selectedBoardingPoint}
                          droppingPoint={currentBusBookingData.selectedDroppingPoint}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </motion.div>
    </>
  );
}

export default function Desktop() {
  const {
    // routing/search context
    navigate,
    from,
    to,
    searchDate,
    setSearchDate,
    searchDateParam,
    operatorKey,

    // options + inputs
    fromOptions,
    toOptions,
    searchFrom,
    setSearchFrom,
    searchTo,
    setSearchTo,
    dateInputRef,
    handleDateContainerClick,
    handleModifySearch,
    todayStr,
    tomorrowStr,

    // data
    loading,
    fetchError,
    visibleBuses,
    sortedBuses,
    availability,
    busDetailsByKey,
    fetchData,
    refreshAvailability,

    // filters
    sortBy,
    setSortBy,
    activeFilterCount,

    // sticky bits
    stickySearchCardRef,
    stickySearchCardOwnHeight,

    // booking state/actions
    expandedBusId,
    handleToggleSeatLayout,
    handleSeatToggle,
    busSpecificBookingData,
    handleBoardingPointSelect,
    handleDroppingPointSelect,
    handleProceedToPayment,
  } = useSearchCore();

  const isOperatorMode = Boolean(operatorKey);

  // ✅ NEW: per-bus "More details" toggle (desktop)
  const [detailsOpen, setDetailsOpen] = useState({});
  const toggleDetails = (busKey) =>
    setDetailsOpen((p) => ({ ...p, [busKey]: !p[busKey] }));

  // ✅ Home-like calendar popup state
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const dateAnchorRef = useRef(null);

  // ✅ NEW: recents (same as Home.jsx) + localStorage persistence
  const [recent, setRecent] = useState(() => {
    try {
      return {
        from: JSON.parse(localStorage.getItem("rb_recent_from") || "[]"),
        to: JSON.parse(localStorage.getItem("rb_recent_to") || "[]"),
      };
    } catch {
      return { from: [], to: [] };
    }
  });

  const pushRecent = (key, city) => {
    setRecent((prev) => {
      const next = { ...prev };
      const list = [city, ...prev[key].filter((c) => c !== city)].slice(0, 5);
      next[key] = list;
      try {
        localStorage.setItem(
          key === "from" ? "rb_recent_from" : "rb_recent_to",
          JSON.stringify(list)
        );
      } catch {}
      return next;
    });
  };

  /* ✅ NEW: RedBus-style navbar hide + search bar "connect" (NO BLUR OVERLAP) */
  const [navOffsetTop, setNavOffsetTop] = useState(0);
  const navElRef = useRef(null);

  // ✅ NEW: compact mode (for the sticky search card)
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const navEl = document.querySelector("header");
    navElRef.current = navEl || null;

    const getNavH = () => (navElRef.current ? navElRef.current.offsetHeight : 0);

    const apply = () => {
      const navH = getNavH();
      const y = window.scrollY || 0;

      // smooth progress: 0 -> 1 between these scroll values
      const START = 6;
      const END = 70;
      const tRaw = (y - START) / (END - START);
      const t = Math.max(0, Math.min(1, tRaw));

      // move navbar up
      if (navElRef.current) {
        navElRef.current.style.willChange = "transform";
        navElRef.current.style.transition = "transform 180ms ease";
        navElRef.current.style.transform = `translateY(${-t * navH}px)`;
      }

      // move sticky search bar up to fill navbar space (no overlap blur)
      setNavOffsetTop(navH * (1 - t));

      // compact toggle similar to RedBus
      setIsCompact(t > 0.85);
    };

    apply();

    const onScroll = () => apply();
    const onResize = () => apply();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (navElRef.current) {
        navElRef.current.style.transform = "";
        navElRef.current.style.transition = "";
        navElRef.current.style.willChange = "";
      }
    };
  }, []);

  const selectStyles = useMemo(() => {
    const valueFont = isCompact ? "16px" : "18px";
    const placeFont = isCompact ? "14px" : "16px";
    return {
      control: (p) => ({
        ...p,
        border: "none",
        boxShadow: "none",
        backgroundColor: "transparent",
        minHeight: "auto",
        height: "auto",
        cursor: "pointer",
      }),
      valueContainer: (p) => ({ ...p, padding: "0" }),
      placeholder: (p) => ({
        ...p,
        color: PALETTE.textLight,
        fontSize: placeFont,
        fontWeight: "500",
      }),
      singleValue: (p) => ({
        ...p,
        color: PALETTE.textDark,
        fontSize: valueFont,
        fontWeight: "600",
      }),
      menu: (p) => ({
        ...p,
        borderRadius: "12px",
        boxShadow:
          "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      }),
      menuPortal: (p) => ({ ...p, zIndex: 9999 }),
      option: (p, state) => ({
        ...p,
        backgroundColor: state.isSelected
          ? PALETTE.primaryRed
          : state.isFocused
          ? "#FEE2E2"
          : PALETTE.white,
        color: state.isSelected ? PALETTE.white : PALETTE.textDark,
        cursor: "pointer",
        padding: "12px 16px",
        transition: "background-color 0.2s ease, color 0.2s ease",
      }),
    };
  }, [isCompact]);

  const filterPanelTopOffset = useMemo(() => {
    // keep the sidebar just below the sticky search card
    return stickySearchCardOwnHeight + 16;
  }, [stickySearchCardOwnHeight]);

  const renderCards = () => {
    if (loading) {
      return Array.from({ length: 5 }).map((_, i) => (
        <BusCardSkeleton key={i} />
      ));
    }
    if (fetchError) {
      return (
        <div className="text-center p-10 bg-white rounded-2xl shadow">
          <h3
            className="text-2xl font-bold mb-2"
            style={{ color: PALETTE.textDark }}
          >
            Oops! Something went wrong.
          </h3>
          <p className="mb-6" style={{ color: PALETTE.textLight }}>
            {fetchError}
          </p>
          <button
            onClick={fetchData}
            className="px-6 py-2.5 font-semibold rounded-lg text-white"
            style={{ backgroundColor: PALETTE.accentBlue }}
          >
            Try again
          </button>
        </div>
      );
    }
    if (!visibleBuses.length) {
      return (
        <div className="text-center p-10 bg-white rounded-2xl shadow">
          <h3
            className="text-2xl font-bold mb-2"
            style={{ color: PALETTE.textDark }}
          >
            {activeFilterCount
              ? "No Buses Match Your Filters"
              : "No Buses Available"}
          </h3>
          <p className="mb-6" style={{ color: PALETTE.textLight }}>
            {activeFilterCount
              ? "Try adjusting or resetting your filters."
              : "No buses were found for this route on the selected date."}
          </p>
        </div>
      );
    }

    return (
      <motion.div variants={listVariants} initial="hidden" animate="visible">
        {visibleBuses.map((bus) => {
          const busKey = `${bus._id}-${bus.departureTime}`;
          const availabilityEntry = availability?.[busKey];

          const detailBus = busDetailsByKey?.[busKey]
            ? { ...bus, ...busDetailsByKey[busKey] }
            : bus;
          const isDetailsLoading =
            expandedBusId === busKey && !busDetailsByKey?.[busKey];

          return (
            <BusCard
              key={busKey}
              bus={bus}
              detailBus={detailBus}
              isDetailsLoading={isDetailsLoading}
              busKey={busKey}
              from={from}
              to={to}
              searchDateParam={searchDateParam}
              availabilityEntry={availabilityEntry}
              expandedBusId={expandedBusId}
              busSpecificBookingData={busSpecificBookingData}
              handleToggleSeatLayout={handleToggleSeatLayout}
              handleSeatToggle={handleSeatToggle}
              handleBoardingPointSelect={handleBoardingPointSelect}
              handleDroppingPointSelect={handleDroppingPointSelect}
              handleProceedToPayment={handleProceedToPayment}
              fetchData={fetchData}
              refreshAvailability={refreshAvailability}
            />
          );
        })}
      </motion.div>
    );
  };

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ backgroundColor: PALETTE.white }}
    >
      {/* Header */}
      <div className="w-full bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="hidden lg:block">
            <div className="flex items-center mb-2">
              <FaChevronLeft
                className="text-xl mr-2 cursor-pointer"
                onClick={() => navigate(-1)}
              />
              <span
                className="text-sm font-medium"
                style={{ color: PALETTE.textLight }}
              >
                Bus Ticket
              </span>
              <span className="mx-1 text-gray-400 text-sm">&gt;</span>
              <span
                className="text-sm font-medium"
                style={{ color: PALETTE.textLight }}
              >
                {from} to {to} Bus
              </span>
            </div>
            <h1
              className="text-2xl font-bold"
              style={{ color: PALETTE.textDark }}
            >
              {from}{" "}
              <FaExchangeAlt className="inline-block mx-2 text-gray-500" /> {to}
            </h1>
            {!loading && !fetchError && (
              <p className="text-sm text-gray-500 mb-4">
                {sortedBuses.length} buses
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Sticky search controls (desktop) */}
      {!isOperatorMode ? (
      <div
        ref={stickySearchCardRef}
        className="sticky z-40 w-full shadow-sm"
        style={{
          top: `${navOffsetTop}px`,
          backgroundColor: PALETTE.white,
        }}
      >
        <div
          className={[
            "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8",
            isCompact ? "py-1.5" : "py-2",
          ].join(" ")}
        >
          <div className="bg-white border border-gray-300 rounded-3xl">
            <div className="hidden lg:flex rounded-2xl">
              <div
                className={[
                  "relative flex-1 flex items-center border-r",
                  isCompact ? "p-3" : "p-4",
                ].join(" ")}
                style={{ borderColor: PALETTE.borderLight }}
              >
                <FaBus className="text-gray-400 mr-4 text-xl shrink-0" />
                <div className="w-full">
                  <div className="flex items-center justify-between">
                    <label
                      className={[
                        "block font-semibold uppercase tracking-wider leading-none",
                        isCompact ? "text-[10px] mb-0.5" : "text-xs",
                      ].join(" ")}
                      style={{ color: PALETTE.textLight }}
                    >
                      From
                    </label>
                  </div>
                  <Select
                    options={fromOptions}
                    value={
                      searchFrom
                        ? { value: searchFrom, label: searchFrom }
                        : null
                    }
                    onChange={(s) => {
                      const v = s?.value || "";
                      setSearchFrom(v);
                      if (v) pushRecent("from", v);
                    }}
                    placeholder="Select departure"
                    isClearable
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null,
                      ClearIndicator: () => null,
                      Menu: CustomMenu("from"),
                    }}
                    recent={recent}
                    closeMenuOnSelect={true}
                  />
                </div>

                <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 z-20">
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 180 }}
                    whileTap={{ scale: 0.9, rotate: 180 }}
                    transition={{ type: "spring", stiffness: 400, damping: 22 }}
                    type="button"
                    className="bg-white w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-all duration-200"
                    style={{ border: `2px solid ${PALETTE.borderLight}` }}
                    title="Swap locations"
                    onClick={() => {
                      setSearchFrom(searchTo);
                      setSearchTo(searchFrom);
                    }}
                  >
                    <FaExchangeAlt style={{ color: PALETTE.textLight }} />
                  </motion.button>
                </div>
              </div>

              <div
                className={[
                  "flex-1 flex items-center border-r",
                  isCompact ? "p-3 lg:pl-10" : "p-4 lg:pl-10",
                ].join(" ")}
                style={{ borderColor: PALETTE.borderLight }}
              >
                <FaBus className="text-gray-400 mr-4 text-xl shrink-0" />
                <div className="w-full">
                  <div className="flex items-center justify-between">
                    <label
                      className={[
                        "block font-semibold uppercase tracking-wider leading-none",
                        isCompact ? "text-[10px] mb-0.5" : "text-xs",
                      ].join(" ")}
                      style={{ color: PALETTE.textLight }}
                    >
                      To
                    </label>
                  </div>
                  <Select
                    options={toOptions}
                    value={searchTo ? { value: searchTo, label: searchTo } : null}
                    onChange={(s) => {
                      const v = s?.value || "";
                      setSearchTo(v);
                      if (v) pushRecent("to", v);
                    }}
                    placeholder="Select destination"
                    isClearable
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null,
                      ClearIndicator: () => null,
                      Menu: CustomMenu("to"),
                    }}
                    recent={recent}
                    closeMenuOnSelect={true}
                  />
                </div>
              </div>

              <div
                className={[
                  "flex-1 flex items-center",
                  isCompact ? "p-3" : "p-4",
                ].join(" ")}
              >
                <FaCalendarAlt className="text-gray-400 mr-4 text-xl shrink-0" />
                <div className="w-full">
                  <label
                    className={[
                      "block font-semibold uppercase tracking-wider leading-none",
                      isCompact ? "text-[10px] mb-0.5" : "text-xs",
                    ].join(" ")}
                    style={{ color: PALETTE.textLight }}
                  >
                    Date of Journey
                  </label>

                  <div
                    ref={dateAnchorRef}
                    onClick={() => setIsCalendarOpen(true)}
                    className="cursor-pointer"
                  >
                    <span
                      className={[
                        "font-medium",
                        isCompact ? "text-base" : "text-lg",
                      ].join(" ")}
                      style={{ color: PALETTE.textDark }}
                    >
                      {getReadableDate(searchDate)}
                    </span>
                  </div>

                  {!isCompact && (
                    <div className="mt-1">
                      <button
                        onClick={() => setSearchDate(todayStr)}
                        className="text-xs font-medium mr-3"
                        style={{
                          color:
                            searchDate === todayStr
                              ? PALETTE.primaryRed
                              : PALETTE.accentBlue,
                        }}
                      >
                        Today
                      </button>
                      <button
                        onClick={() => setSearchDate(tomorrowStr)}
                        className="text-xs font-medium"
                        style={{
                          color:
                            searchDate === tomorrowStr
                              ? PALETTE.primaryRed
                              : PALETTE.accentBlue,
                        }}
                      >
                        Tomorrow
                      </button>
                    </div>
                  )}
                </div>

                <input
                  ref={dateInputRef}
                  type="date"
                  value={searchDate}
                  onChange={(e) => setSearchDate(e.target.value)}
                  min={todayStr}
                  className="absolute opacity-0 pointer-events-none"
                />

                <CalendarPopover
                  open={isCalendarOpen}
                  anchorRef={dateAnchorRef}
                  value={searchDate}
                  minDateStr={todayStr}
                  onChange={setSearchDate}
                  onClose={() => setIsCalendarOpen(false)}
                />
              </div>

              <div
                className={[
                  "p-3 flex items-center gap-3",
                  isCompact ? "pr-3" : "",
                ].join(" ")}
              >
                {isCompact && (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSearchDate(todayStr)}
                      className="text-xs font-semibold"
                      style={{
                        color:
                          searchDate === todayStr
                            ? PALETTE.primaryRed
                            : PALETTE.accentBlue,
                      }}
                    >
                      Today
                    </button>
                    <button
                      onClick={() => setSearchDate(tomorrowStr)}
                      className="text-xs font-semibold"
                      style={{
                        color:
                          searchDate === tomorrowStr
                            ? PALETTE.primaryRed
                            : PALETTE.accentBlue,
                      }}
                    >
                      Tomorrow
                    </button>
                  </div>
                )}

                <button
                  onClick={handleModifySearch}
                  className="font-heading w-full lg:w-auto flex items-center justify-center gap-2 text-white font-bold tracking-wider px-8 py-3 rounded-full shadow-lg"
                  style={{ backgroundColor: PALETTE.primaryRed }}
                >
                  <FaSearch /> Search Buses
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      ) : (
        <div
          ref={stickySearchCardRef}
          className="sticky z-40 w-full shadow-sm"
          style={{
            top: `${navOffsetTop}px`,
            backgroundColor: PALETTE.white,
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="bg-white border border-red-100 rounded-3xl px-5 py-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-red-500">
                  Operator booking mode
                </p>
                <h2 className="text-lg font-bold text-gray-900">
                  {from} <FaExchangeAlt className="inline-block mx-2 text-gray-400" /> {to}
                </h2>
                <p className="text-sm text-gray-500">
                  {getReadableDate(searchDateParam)} • Showing only this operator's buses
                </p>
              </div>
              {!loading && !fetchError && (
                <div className="rounded-full bg-red-50 px-4 py-2 text-sm font-bold text-red-600">
                  {sortedBuses.length} buses
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Page content */}
      <div
        className="flex-1 w-full pb-10"
        style={{
          background: "linear-gradient(160deg, #f8f0f0 0%, #f1e8e8 15%, #ede8ee 35%, #e8eaf2 55%, #eceef5 75%, #eef1f7 100%)",
          borderTop: `1px solid ${PALETTE.borderLight}` ,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 lg:gap-8 items-start">
            {/* Sidebar filters (desktop) */}
            <aside
              className="hidden lg:block lg:col-span-1 sticky"
              style={{ top: `${filterPanelTopOffset}px`, zIndex: 20 }}
            >
              <FilterPanel
                isMobile={false}
                sortBy={sortBy}
                setSortBy={setSortBy}
              />
            </aside>

            {/* Main column */}
            <main className="lg:col-span-3 space-y-5">
              <SpecialNoticesSection />
              <AnimatePresence>
                {loading
                  ? Array.from({ length: 5 }).map((_, i) => (
                      <BusCardSkeleton key={i} />
                    ))
                  : renderCards()}
              </AnimatePresence>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}






